import { Link, useParams } from "wouter";
import { ArrowLeft } from "lucide-react";
import { useClub, useClubPost } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Loader } from "@/components/Loader";
import { ClubPostCard, GateNotice } from "./clubComponents";
import { ClubComposer } from "./ClubComposer";
import { useClubGate } from "./useClubGate";

export default function ClubPostThread() {
  const { slug, postId } = useParams<{ slug: string; postId: string }>();
  const { t } = useT();
  const { data: club } = useClub(slug);
  const { data: thread, isLoading, error } = useClubPost(slug, postId);
  const gate = useClubGate();

  const back = (
    <Link href={`/clubs/${slug}/forum`} className="btn-ghost inline-flex items-center gap-2">
      <ArrowLeft className="w-4 h-4" /> {t("clubs.forum")}
    </Link>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  if (error || !thread) {
    return (
      <div className="pt-4 pb-24 md:pt-8 px-4 max-w-3xl mx-auto space-y-6">
        {back}
        <div className="card text-center py-12 text-dark-muted">{error?.message ?? t("clubs.notFound")}</div>
      </div>
    );
  }

  const canWrite = !!club?.access.can.writeForum;

  return (
    <div className="pt-4 pb-24 md:pt-8 px-4">
      <div className="max-w-3xl mx-auto space-y-4">
        {back}
        <ClubPostCard post={thread.post} slug={slug} linkToThread={false} />
        <div className="pl-4 border-l-2 border-neutral-200 dark:border-neutral-800 space-y-3">
          {thread.replies.map((reply) => (
            <ClubPostCard key={reply.id} post={reply} slug={slug} />
          ))}
          {canWrite ? (
            <ClubComposer slug={slug} parentId={thread.post.id} />
          ) : (
            club && (
              <GateNotice
                message={club.access.isMember ? t("clubs.subscribeToPost") : t("clubs.joinToParticipate")}
                actionLabel={club.access.isMember ? t("clubs.subscribe") : undefined}
                onAction={gate.openSubscribe}
              />
            )
          )}
        </div>
      </div>
      {gate.interstitial}
    </div>
  );
}
